import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Flower2, 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Home,
  Gift,
  User,
  Utensils,
  Coffee,
  Flame,
  Droplets,
  Palette,
  Building,
  Wine
} from 'lucide-react';
import { toast } from 'sonner';

// Ceramic product types
const PRODUCT_TYPES = [
  { value: 'vaso', label: 'Vaso decorativo', icon: Flower2 },
  { value: 'piatti', label: 'Piatti / Set da tavola', icon: Utensils },
  { value: 'tazze', label: 'Tazze / Mug', icon: Coffee },
  { value: 'ciotole', label: 'Ciotole / Bowl', icon: Utensils },
  { value: 'brocca', label: 'Brocca / Caraffa', icon: Wine },
  { value: 'portacandele', label: 'Portacandele', icon: Flame },
  { value: 'centrotavola', label: 'Centrotavola', icon: Flower2 },
  { value: 'portavaso', label: 'Portavaso / Cache-pot', icon: Flower2 },
  { value: 'scultura', label: 'Scultura decorativa', icon: Star },
  { value: 'piastrelle', label: 'Piastrelle / Mattonelle', icon: Building },
  { value: 'numero_civico', label: 'Numero civico', icon: Home },
  { value: 'bomboniera', label: 'Bomboniere', icon: Gift },
  { value: 'altro', label: 'Altro', icon: Sparkles },
];

// Ceramic techniques/styles
const TECHNIQUES = [
  { value: 'maiolica', label: 'Maiolica tradizionale', icon: Palette },
  { value: 'terracotta', label: 'Terracotta', icon: Flame },
  { value: 'gres', label: 'Gres porcellanato', icon: Droplets },
  { value: 'raku', label: 'Raku', icon: Flame },
  { value: 'porcellana', label: 'Porcellana', icon: Star },
  { value: 'bucchero', label: 'Bucchero etrusco', icon: Flame },
  { value: 'dipinto_mano', label: 'Dipinto a mano', icon: Palette },
  { value: 'smaltato', label: 'Smaltato lucido', icon: Sparkles },
  { value: 'opaco', label: 'Finitura opaca/matt', icon: Droplets },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Decoration styles
const DECORATION_STYLES = [
  { value: 'mediterraneo', label: 'Mediterraneo / Costiera', icon: Droplets },
  { value: 'siciliano', label: 'Siciliano / Teste di Moro', icon: Star },
  { value: 'toscano', label: 'Toscano / Rinascimentale', icon: Palette },
  { value: 'deruta', label: 'Deruta / Umbro', icon: Palette },
  { value: 'vietrese', label: 'Vietrese / Amalfitano', icon: Droplets },
  { value: 'faentino', label: 'Faentino', icon: Star },
  { value: 'moderno', label: 'Moderno / Minimalista', icon: Sparkles },
  { value: 'astratto', label: 'Astratto / Artistico', icon: Palette },
  { value: 'botanico', label: 'Botanico / Floreale', icon: Flower2 },
  { value: 'geometrico', label: 'Geometrico', icon: Building },
  { value: 'personalizzato', label: 'Personalizzato', icon: Sparkles },
];

// Destination/Use
const DESTINATIONS = [
  { value: 'casa_soggiorno', label: 'Casa - Soggiorno', icon: Home },
  { value: 'casa_cucina', label: 'Casa - Cucina / Tavola', icon: Utensils },
  { value: 'casa_bagno', label: 'Casa - Bagno', icon: Droplets },
  { value: 'giardino', label: 'Giardino / Terrazzo', icon: Flower2 },
  { value: 'ristorante', label: 'Ristorante / Hotel', icon: Building },
  { value: 'regalo', label: 'Regalo', icon: Gift },
  { value: 'bomboniere', label: 'Bomboniere / Evento', icon: Gift },
  { value: 'collezione', label: 'Collezione / Esposizione', icon: Star },
  { value: 'altro', label: 'Altro', icon: Sparkles },
];

// Size options
const SIZE_OPTIONS = [
  { value: 'piccolo', label: 'Piccolo (fino a 15cm)', description: 'Tazze, piccoli vasi' },
  { value: 'medio', label: 'Medio (15-30cm)', description: 'Piatti, ciotole, vasi medi' },
  { value: 'grande', label: 'Grande (30-50cm)', description: 'Vasi grandi, centrotavola' },
  { value: 'molto_grande', label: 'Molto grande (oltre 50cm)', description: 'Anfore, sculture' },
  { value: 'set', label: 'Set di più pezzi', description: 'Servizi, collezioni' },
  { value: 'non_so', label: 'Non so ancora', description: 'Vorrei consiglio' },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '30-70', label: '€30 - €70', description: 'Piccoli oggetti singoli' },
  { value: '70-150', label: '€70 - €150', description: 'Oggetti medi decorati' },
  { value: '150-300', label: '€150 - €300', description: 'Pezzi pregiati' },
  { value: '300-500', label: '€300 - €500', description: 'Set o pezzi grandi' },
  { value: '500-1000', label: '€500 - €1.000', description: 'Opere elaborate' },
  { value: '1000+', label: '€1.000+', description: 'Servizi completi / Opere uniche' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '2weeks', label: '2 Settimane', description: 'Urgente' },
  { value: '3weeks', label: '3 Settimane', description: 'Prioritario' },
  { value: '1month', label: '1 Mese', description: 'Standard' },
  { value: '2months', label: '2 Mesi', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example products
const CERAMIC_EXAMPLES = [
  {
    idea: "Set di piatti per il mio ristorante sul mare",
    result: "Servizio 24 piatti in maiolica vietrese con decoro limoni e onde blu, dipinti a mano",
    image: "https://images.unsplash.com/photo-1762926627840-6caf5516145e?w=400&h=300&fit=crop",
    category: "Set da tavola"
  },
  {
    idea: "Vaso decorativo grande per ingresso",
    result: "Anfora 60cm in ceramica di Caltagirone con decorazioni siciliane, teste di moro stilizzate",
    image: "https://images.unsplash.com/photo-1631125915902-d8abe9225ff2?w=400&h=300&fit=crop",
    category: "Vasi"
  },
  {
    idea: "Bomboniere personalizzate per matrimonio",
    result: "50 piccoli vasetti portapiante in terracotta smaltata con iniziali degli sposi incise",
    image: "https://images.unsplash.com/photo-1590605095243-072811dbe64c?w=400&h=300&fit=crop",
    category: "Bomboniere"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho ordinato un servizio di piatti per il mio agriturismo. Ogni pezzo è unico e i clienti li adorano. La ceramica artigianale fa davvero la differenza!",
    author: "Francesca M.",
    location: "Toscana"
  },
  {
    text: "Cercavo un vaso particolare per un angolo del salotto. L'artigiano ha interpretato perfettamente il mio stile, un pezzo che attira sempre complimenti.",
    author: "Andrea R.",
    location: "Milano"
  },
  {
    text: "Le bomboniere in ceramica per il battesimo di mia figlia erano meravigliose. Qualità altissima e prezzo onesto. Tutti gli invitati entusiasti!",
    author: "Valentina P.",
    location: "Napoli"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Che tipo di ceramiche posso richiedere?",
    answer: "Puoi richiedere qualsiasi tipo di ceramica artigianale: vasi decorativi, set di piatti e stoviglie, tazze, ciotole, brocche, portacandele, centrotavola, sculture, piastrelle decorative, numeri civici, bomboniere e molto altro."
  },
  {
    question: "Quali stili di decorazione sono disponibili?",
    answer: "I nostri ceramisti padroneggiano tutti gli stili della tradizione italiana: maiolica mediterranea, ceramica vietrese, siciliana con teste di moro, Deruta, faentina, toscana rinascimentale. Sono disponibili anche stili moderni, minimalisti e decorazioni completamente personalizzate."
  },
  {
    question: "Posso personalizzare completamente il design?",
    answer: "Certamente! Puoi richiedere decorazioni personalizzate con nomi, date, iniziali, loghi, immagini specifiche o design completamente originali. Gli artigiani possono anche riprodurre un decoro da una foto di riferimento."
  },
  {
    question: "Le ceramiche sono adatte all'uso alimentare?",
    answer: "Sì, la maggior parte delle ceramiche per tavola (piatti, tazze, ciotole) sono realizzate con smalti atossici certificati per uso alimentare e sono lavabili in lavastoviglie. Specifica sempre l'uso previsto nella tua richiesta."
  },
  {
    question: "Quanto tempo ci vuole per la realizzazione?",
    answer: "I tempi variano: un pezzo singolo richiede 2-4 settimane (inclusa cottura e decorazione), un set completo 4-6 settimane. Pezzi molto elaborati o grandi quantità possono richiedere fino a 2 mesi."
  }
];

export default function CeramicheRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    product_type: '',
    technique: '',
    decoration_style: '',
    destination: '',
    size: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Ceramiche su Misura | Artigiani Italiani | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `ceramic-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi la ceramica che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_ceramic_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const productLabel = PRODUCT_TYPES.find(t => t.value === formData.product_type)?.label || '';
      const techniqueLabel = TECHNIQUES.find(t => t.value === formData.technique)?.label || '';
      const decorationLabel = DECORATION_STYLES.find(d => d.value === formData.decoration_style)?.label || '';
      const destinationLabel = DESTINATIONS.find(d => d.value === formData.destination)?.label || '';
      const sizeLabel = SIZE_OPTIONS.find(s => s.value === formData.size)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      
      const details = [];
      if (productLabel) details.push(`🏺 Tipo: ${productLabel}`);
      if (techniqueLabel) details.push(`🔥 Tecnica: ${techniqueLabel}`);
      if (decorationLabel) details.push(`🎨 Stile decorazione: ${decorationLabel}`);
      if (destinationLabel) details.push(`🏠 Destinazione: ${destinationLabel}`);
      if (sizeLabel) details.push(`📐 Dimensioni: ${sizeLabel}`);
      if (budgetLabel) details.push(`💰 Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️ Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${productLabel} in ceramica su misura`.trim() || 'Ceramica artigianale su misura',
        description: fullDescription,
        category: 'Ceramiche',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dai ceramisti.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('ceramic-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Ceramiche su Misura | Maioliche, Vasi, Piatti Artigianali | Rarifex</title>
        <meta name="description" content="Commissiona ceramiche artigianali su misura da ceramisti italiani. Maioliche, vasi, piatti, set da tavola dipinti a mano. Stili: vietrese, siciliano, Deruta, toscano. Gratuito e senza impegno." />
        <meta name="keywords" content="ceramiche su misura, maiolica italiana, ceramica artigianale, vasi dipinti a mano, piatti ceramica, Deruta, Vietri, ceramica siciliana, Made in Italy" />
        <link rel="canonical" href="https://www.rarifex.com/ceramiche-su-misura" />
        <meta property="og:title" content="Ceramiche su Misura | Artigiani Italiani | Rarifex" />
        <meta property="og:description" content="Commissiona ceramiche artigianali da ceramisti italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/ceramiche-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-sky-50/50 via-white to-blue-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-sky-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Flower2 className="w-4 h-4" />
                    Ceramiche Artigianali
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Ceramica italiana{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-blue-600">
                      creata per te
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Maioliche, vasi, piatti e decorazioni su misura.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Ceramisti italiani realizzano pezzi unici con tecniche tradizionali: maiolica, terracotta, gres, decorazioni dipinte a mano.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Dipinto a mano</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-sky-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <Card id="ceramic-form" className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold text-slate-800">Descrivi la tua ceramica</h2>
                      <p className="text-slate-500 text-sm mt-1">Ricevi proposte da ceramisti italiani</p>
                    </div>
                    
                    {/* Title */}
                    <div>
                      <Label className="text-slate-700">Titolo progetto (opzionale)</Label>
                      <Input
                        placeholder="Es: Set piatti per ristorante, Vaso per ingresso..."
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    
                    {/* Description */}
                    <div>
                      <Label className="text-slate-700">Descrivi cosa desideri *</Label>
                      <Textarea
                        placeholder="Descrivi la ceramica che vorresti: tipo, dimensioni, colori, decorazioni, quantità se set..."
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        className="mt-1 min-h-[100px]"
                        required
                      />
                    </div>
                    
                    {/* Product Type & Technique Row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-700">Tipo di ceramica</Label>
                        <select
                          value={formData.product_type}
                          onChange={(e) => handleInputChange('product_type', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                        >
                          <option value="">Seleziona...</option>
                          {PRODUCT_TYPES.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-slate-700">Tecnica / Materiale</Label>
                        <select
                          value={formData.technique}
                          onChange={(e) => handleInputChange('technique', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                        >
                          <option value="">Seleziona...</option>
                          {TECHNIQUES.map(tech => (
                            <option key={tech.value} value={tech.value}>{tech.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Decoration Style */}
                    <div>
                      <Label className="text-slate-700">Stile decorazione</Label>
                      <select
                        value={formData.decoration_style}
                        onChange={(e) => handleInputChange('decoration_style', e.target.value)}
                        className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                      >
                        <option value="">Seleziona...</option>
                        {DECORATION_STYLES.map(style => (
                          <option key={style.value} value={style.value}>{style.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Destination & Size Row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-700">Destinazione</Label>
                        <select
                          value={formData.destination}
                          onChange={(e) => handleInputChange('destination', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                        >
                          <option value="">Seleziona...</option>
                          {DESTINATIONS.map(dest => (
                            <option key={dest.value} value={dest.value}>{dest.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-slate-700">Dimensioni</Label>
                        <select
                          value={formData.size}
                          onChange={(e) => handleInputChange('size', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                        >
                          <option value="">Seleziona...</option>
                          {SIZE_OPTIONS.map(size => (
                            <option key={size.value} value={size.value}>{size.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Budget & Timeline Row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-700">Budget</Label>
                        <select
                          value={formData.budget}
                          onChange={(e) => handleInputChange('budget', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                        >
                          <option value="">Seleziona...</option>
                          {BUDGET_OPTIONS.map(budget => (
                            <option key={budget.value} value={budget.value}>{budget.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-slate-700">Tempistiche</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(timeline => (
                            <option key={timeline.value} value={timeline.value}>{timeline.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Image Upload */}
                    <div>
                      <Label className="text-slate-700">Immagini di riferimento (opzionale)</Label>
                      <div className="mt-2 border-2 border-dashed border-slate-200 rounded-lg p-4 hover:border-sky-300 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleFileUpload}
                          className="hidden"
                          id="ceramic-images"
                          disabled={uploadingFile}
                        />
                        <label htmlFor="ceramic-images" className="cursor-pointer flex flex-col items-center gap-2">
                          {uploadingFile ? (
                            <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
                          ) : (
                            <Upload className="w-8 h-8 text-slate-400" />
                          )}
                          <span className="text-sm text-slate-500">
                            {uploadingFile ? 'Caricamento...' : 'Clicca per caricare (max 5 immagini)'}
                          </span>
                        </label>
                      </div>
                      
                      {/* Uploaded images preview */}
                      {formData.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {formData.attachments.map((url, index) => (
                            <div key={index} className="relative w-16 h-16 rounded-lg overflow-hidden group">
                              <img src={url} alt="" className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => removeAttachment(index)}
                                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              >
                                <X className="w-5 h-5 text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Submit Button */}
                    <Button 
                      type="submit" 
                      className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700"
                      disabled={loading}
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <Sparkles className="w-5 h-5 mr-2" />
                      )}
                      {loading ? 'Invio in corso...' : 'Ricevi proposte gratis'}
                    </Button>
                    
                    <p className="text-xs text-slate-400 text-center">
                      Gratuito e senza impegno. Riceverai proposte entro 24-48 ore.
                    </p>
                  </form>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Come funziona
              </h2>
              <p className="text-lg text-slate-600">
                Dalla tua idea alla ceramica finita in 4 semplici passi
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-4 gap-8">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Racconta la ceramica che desideri: tipo, stile, colori, decorazioni' },
                { icon: Users, title: 'Ricevi', desc: 'I ceramisti interessati ti inviano le loro proposte con bozzetti' },
                { icon: CheckCircle, title: 'Scegli', desc: 'Valuta qualità, portfolio e preventivi per scegliere il migliore' },
                { icon: Heart, title: 'Ammira', desc: 'Segui la lavorazione e ricevi il tuo pezzo unico' },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-500 flex items-center justify-center text-white shadow-lg">
                    <step.icon className="w-8 h-8" />
                  </div>
                  <div className="text-sm font-semibold text-sky-600 mb-1">Passo {index + 1}</div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Esempi di ceramiche realizzate
              </h2>
              <p className="text-lg text-slate-600">
                Dall'idea al pezzo finito
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
              {CERAMIC_EXAMPLES.map((example, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="aspect-[4/3] relative">
                    <img src={example.image} alt={example.category} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3">
                      <span className="bg-sky-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        {example.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="mb-3">
                      <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">L'idea</div>
                      <p className="text-slate-700 font-medium">{example.idea}</p>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Il risultato</div>
                      <p className="text-slate-600 text-sm">{example.result}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa dicono i nostri clienti
              </h2>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
              {TESTIMONIALS.map((testimonial, index) => (
                <Card key={index} className="p-6 bg-gradient-to-br from-sky-50 to-blue-50 border-0">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-sky-200 flex items-center justify-center text-sky-700 font-bold">
                      {testimonial.author[0]}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800">{testimonial.author}</div>
                      <div className="text-sm text-slate-500">{testimonial.location}</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Domande frequenti
                </h2>
              </div>
              
              <div className="space-y-3">
                {FAQ_ITEMS.map((faq, index) => (
                  <Card 
                    key={index} 
                    className={`overflow-hidden transition-all cursor-pointer ${expandedFaq === index ? 'ring-2 ring-sky-200' : ''}`}
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="p-4 flex items-center justify-between">
                      <h3 className="font-semibold text-slate-800 pr-4">{faq.question}</h3>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {expandedFaq === index && (
                      <div className="px-4 pb-4 text-slate-600 border-t border-slate-100 pt-3">
                        {faq.answer}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-sky-600 to-blue-600">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-3xl mx-auto">
              <Flower2 className="w-16 h-16 mx-auto mb-6 text-white/90" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Pronto a creare la tua ceramica?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Descrivi la tua idea e ricevi proposte da ceramisti italiani. Gratuito e senza impegno.
              </p>
              <Button 
                size="lg" 
                className="bg-white text-sky-600 hover:bg-sky-50 font-semibold text-lg px-8 py-6 h-auto shadow-xl"
                onClick={scrollToForm}
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Inizia ora
              </Button>
            </div>
          </div>
        </section>
      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-800 mb-2">Accedi per inviare la richiesta</h3>
            <p className="text-slate-600 mb-6">
              Per ricevere proposte dai ceramisti devi accedere o creare un account gratuito.
            </p>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowAuthPrompt(false)}
              >
                Annulla
              </Button>
              <Button 
                className="flex-1 bg-sky-600 hover:bg-sky-700"
                onClick={() => navigate('/login?redirect=/ceramiche-su-misura')}
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
